import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { History, ChevronLeft, ChevronRight, User, Package, FileText, ShoppingBasket, ChefHat, ClipboardList } from 'lucide-react';

const ENTITY_TYPES = [
    { value: 'all', label: 'Tous les types' },
    { value: 'USER', label: 'Utilisateurs' },
    { value: 'PRODUCT', label: 'Produits' },
    { value: 'CATEGORY', label: 'Categories' },
    { value: 'PURCHASE', label: 'Achats' },
    { value: 'SHOPPING_LIST', label: 'Listes de courses' },
    { value: 'RECIPE', label: 'Recettes' },
    { value: 'DAILY_OUTPUT', label: 'Sorties du jour' },
    { value: 'STOCK_MOVEMENT', label: 'Mouvements de stock' }
];

const ACTIONS = [
    { value: 'all', label: 'Toutes les actions' },
    { value: 'CREATE', label: 'Creation' },
    { value: 'UPDATE', label: 'Modification' },
    { value: 'DELETE', label: 'Suppression' }
];

const AuditLogPage = () => {
    const { api } = useAuth();
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [filterAction, setFilterAction] = useState('all');
    const pageSize = 20;

    useEffect(() => {
        fetchLogs();
    }, [page, filterType, filterAction]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString()
            });
            if (filterType !== 'all') params.append('entity_type', filterType);
            if (filterAction !== 'all') params.append('action', filterAction);
            
            const response = await api().get(`/admin/audit-logs?${params}`);
            setLogs(response.data.logs);
            setTotal(response.data.total);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const getEntityIcon = (type) => {
        const icons = {
            'USER': User,
            'PRODUCT': Package,
            'CATEGORY': FileText,
            'SHOPPING_LIST': ShoppingBasket,
            'RECIPE': ChefHat,
            'DAILY_OUTPUT': ClipboardList,
            'PURCHASE': FileText,
            'STOCK_MOVEMENT': Package
        };
        const Icon = icons[type] || FileText;
        return <Icon className="w-4 h-4" />;
    };

    const getActionColor = (action) => {
        const colors = {
            'CREATE': 'bg-green-500',
            'UPDATE': 'bg-blue-500',
            'DELETE': 'bg-red-500'
        };
        return colors[action] || 'bg-gray-500';
    };

    const getActionLabel = (action) => {
        const labels = {
            'CREATE': 'Creation',
            'UPDATE': 'Modification',
            'DELETE': 'Suppression'
        };
        return labels[action] || action;
    };

    const formatValue = (value) => {
        if (!value) return '-';
        if (typeof value === 'object') {
            return Object.entries(value)
                .filter(([k, v]) => v !== null && v !== undefined)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ');
        }
        return String(value);
    };

    const totalPages = Math.ceil(total / pageSize);

    if (loading && logs.length === 0) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="audit-log-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Journal d'audit
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Historique de toutes les actions sensibles
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(1); }}>
                        <SelectTrigger className="w-44">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ENTITY_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ACTIONS.map(a => (
                                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Logs Table */}
            <Card>
                <CardContent className="p-0">
                    {logs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <History className="w-12 h-12 text-muted-foreground/50 mb-3" />
                            <p className="text-muted-foreground">Aucun evenement trouve</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-44">Date / Heure</TableHead>
                                    <TableHead>Utilisateur</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Element</TableHead>
                                    <TableHead className="hidden lg:table-cell">Details</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {logs.map(log => (
                                    <TableRow key={log.id}>
                                        <TableCell className="text-sm">
                                            <div>
                                                {new Date(log.timestamp).toLocaleDateString('fr-FR')}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {new Date(log.timestamp).toLocaleTimeString('fr-FR')}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium text-sm">{log.user_name}</div>
                                            <div className="text-xs text-muted-foreground">{log.user_role}</div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge 
                                                variant="secondary" 
                                                className={`text-xs text-white ${getActionColor(log.action)}`}
                                            >
                                                {getActionLabel(log.action)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {getEntityIcon(log.entity_type)}
                                                <span className="text-sm">{log.entity_type}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm font-medium">
                                                {log.entity_name || log.entity_id?.substring(0, 8) || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell max-w-xs">
                                            <div className="text-xs text-muted-foreground truncate">
                                                {log.action === 'UPDATE' ? (
                                                    <span>
                                                        {formatValue(log.old_value)} → {formatValue(log.new_value)}
                                                    </span>
                                                ) : log.action === 'CREATE' ? (
                                                    formatValue(log.new_value)
                                                ) : log.action === 'DELETE' ? (
                                                    formatValue(log.old_value)
                                                ) : '-'}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        Page {page} sur {totalPages} ({total} evenements)
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLogPage;
